/**
 * Browser API client — `import { apiFetch, apiUpload } from "@d12labs/client"`.
 *
 * A thin `fetch` wrapper for same-origin API calls: sets credentials, defaults
 * `Content-Type: application/json` (skipped for a `FormData` body so the browser
 * writes the multipart boundary itself), merges caller headers over the defaults,
 * throws the server-provided `error` message on a non-2xx response, and returns
 * the parsed JSON body. Browser-only — imports nothing server-side, so it is safe
 * in the client bundle (it is NOT part of @d12labs/server).
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
	const headers = new Headers();
	if (!(init?.body instanceof FormData)) headers.set("Content-Type", "application/json");
	new Headers(init?.headers).forEach((value, key) => {
		headers.set(key, value);
	});
	const res = await fetch(path, { credentials: "same-origin", ...init, headers });
	if (!res.ok) {
		const err = (await res.json().catch(() => ({ error: res.statusText }))) as {
			error?: string;
		};
		throw new Error(err.error ?? "Request failed");
	}
	return (await res.json()) as T;
}

/** POST a multipart `FormData` body through `apiFetch` (no JSON Content-Type). */
export function apiUpload<T>(path: string, formData: FormData, init?: RequestInit): Promise<T> {
	return apiFetch<T>(path, { method: "POST", ...init, body: formData });
}

export * from "./typed.ts";
